import { useState, useEffect, useCallback } from 'react';
import { webvonkClient as sb } from '../lib/supabase';

const PERIOD_DAYS = { today: 1, week: 7, month: 30, all: null };

function periodStart(period) {
  if (!PERIOD_DAYS[period]) return null;
  const d = new Date();
  d.setDate(d.getDate() - PERIOD_DAYS[period]);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function applyPeriod(query, from) {
  if (from) return query.gte('created_at', from);
  return query;
}

export function useWhenconnectStats(period) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const from = periodStart(period);

    // ── Sessies (excl. intern) ───────────────────────────────
    let sessQ = sb.from('wc_sessions').select('*').eq('is_internal', false);
    sessQ = applyPeriod(sessQ, from);
    const { data: sessions } = await sessQ;

    // ── Pageviews (alleen van externe sessies) ───────────────
    const externalSessionIds = (sessions || []).map(s => s.session_id);

    let pvQ = sb.from('wc_pageviews').select('*');
    pvQ = applyPeriod(pvQ, from);
    if (externalSessionIds.length) pvQ = pvQ.in('session_id', externalSessionIds);
    const { data: pageviews } = externalSessionIds.length ? await pvQ : { data: [] };

    // ── Events (alleen van externe sessies) ──────────────────
    let evQ = sb.from('wc_events').select('*');
    evQ = applyPeriod(evQ, from);
    if (externalSessionIds.length) evQ = evQ.in('session_id', externalSessionIds);
    const { data: events } = externalSessionIds.length ? await evQ : { data: [] };

    setData(compute(sessions || [], pageviews || [], events || []));
    setLastFetched(new Date());
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, lastFetched, reload: load };
}

function compute(sessions, pageviews, events) {
  const total       = sessions.length;
  const uniqueVis   = new Set(sessions.map(s => s.visitor_hash)).size;
  const returning   = sessions.filter(s => s.is_returning).length;
  const newVis      = total - returning;
  const bounces     = sessions.filter(s => (s.page_count ?? 0) <= 1).length;
  const bounceRate  = total ? Math.round((bounces / total) * 100) : 0;

  const durations   = sessions.map(s => s.duration_sec).filter(d => d > 0);
  const avgDuration = durations.length
    ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
    : 0;

  const avgPages = total
    ? (sessions.reduce((a, s) => a + (s.page_count || 0), 0) / total).toFixed(1)
    : '0';

  // Scroll depth
  const scrolls = pageviews.map(p => p.scroll_depth).filter(s => s > 0);
  const avgScroll = scrolls.length
    ? Math.round(scrolls.reduce((a, b) => a + b, 0) / scrolls.length)
    : 0;

  // Pagina's
  const pageMap = {};
  pageviews.forEach(p => {
    if (!p.page) return;
    if (!pageMap[p.page]) pageMap[p.page] = { views: 0, scrolls: [], durations: [] };
    pageMap[p.page].views++;
    if (p.scroll_depth) pageMap[p.page].scrolls.push(p.scroll_depth);
    if (p.duration_sec) pageMap[p.page].durations.push(p.duration_sec);
  });
  const topPages = Object.entries(pageMap)
    .map(([page, d]) => ({
      page,
      views: d.views,
      avgScroll: d.scrolls.length ? Math.round(d.scrolls.reduce((a, b) => a + b, 0) / d.scrolls.length) : 0,
      avgTime: d.durations.length ? Math.round(d.durations.reduce((a, b) => a + b, 0) / d.durations.length) : 0,
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 6);

  // Apparaten
  const deviceMap = countBy(sessions, 'device');
  // Browsers
  const browserMap = countBy(sessions, 'browser');
  // OS
  const osMap = countBy(sessions, 'os');
  // Talen
  const langMap = countBy(sessions, 'language');

  // Verkeersbronnen
  const sourceMap = {};
  sessions.forEach(s => {
    const key = s.referrer_domain || 'Direct';
    sourceMap[key] = (sourceMap[key] || 0) + 1;
  });
  const topSources = Object.entries(sourceMap)
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);

  // UTM campagnes
  const utmMap = {};
  sessions.filter(s => s.utm_campaign).forEach(s => {
    const key = s.utm_campaign;
    utmMap[key] = (utmMap[key] || 0) + 1;
  });
  const topUtm = Object.entries(utmMap)
    .map(([campaign, count]) => ({ campaign, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  // Events
  const ctaClicks    = events.filter(e => e.event_type === 'cta_click').length;
  const formStarts   = events.filter(e => e.event_type === 'form_start').length;
  const formSubmits  = events.filter(e => e.event_type === 'form_submit').length;
  const formConv     = formStarts ? Math.round((formSubmits / formStarts) * 100) : 0;

  // Top buttons
  const btnMap = {};
  events.filter(e => e.event_type === 'button_click' || e.event_type === 'cta_click').forEach(e => {
    const key = e.element_text || e.element_id || '?';
    btnMap[key] = (btnMap[key] || 0) + 1;
  });
  const topButtons = Object.entries(btnMap)
    .map(([text, count]) => ({ text, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Scroll milestones
  const scrollMilestones = [25, 50, 75, 90, 100].map(pct => {
    const count = events.filter(e => e.event_type === 'scroll_milestone' && e.scroll_pct === pct).length;
    const percentage = total ? Math.round((count / total) * 100) : 0;
    return { pct, count, percentage };
  });

  // Recente sessies
  const recentSessions = [...sessions]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8)
    .map(s => ({
      device: s.device,
      browser: s.browser,
      os: s.os,
      duration: s.duration_sec,
      pages: s.page_count,
      source: s.referrer_domain || 'Direct',
      isReturning: s.is_returning,
      isInternal: s.is_internal,
      createdAt: s.created_at,
      landing: s.landing_page,
    }));

  return {
    total, uniqueVis, returning, newVis,
    bounceRate, avgDuration, avgPages, avgScroll,
    totalPageviews: pageviews.length,
    topPages, deviceMap, browserMap, osMap, langMap,
    topSources, topUtm,
    ctaClicks, formStarts, formSubmits, formConv,
    topButtons, scrollMilestones, recentSessions,
  };
}

function countBy(arr, key) {
  const map = {};
  arr.forEach(item => {
    const val = item[key] || 'Onbekend';
    map[val] = (map[val] || 0) + 1;
  });
  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
}
